import axios from 'axios';
import { baseURL } from '../config';
import { convertVideoForWeb } from './videoConvert';

export const STAGE_LABEL = {
    checking: 'Checking for duplicates...',
    converting: 'Optimizing video for playback...',
    uploading: 'Uploading...',
    finalizing: 'Finalizing...',
};

// Stages that report a percentage, so the progress bar is shown.
export const showsProgress = (stage) => stage === 'converting' || stage === 'uploading';

const sha256Hex = async (file) => {
    const buffer = await file.arrayBuffer();
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', buffer);
    return Array.from(new Uint8Array(hashBuffer)).map((b) => b.toString(16).padStart(2, '0')).join('');
};

// Uploads a file straight to S3 via a presigned URL (bypassing our server for
// the file bytes), so progress/cancel reflect the real S3 transfer. Dedupes
// by content checksum first so an identical file is reused instead of
// re-uploaded. Tutorial videos are converted to a web-friendly MP4 first (the
// duplicate check and stored checksum stay on the original file, so uploading the
// same video again is still caught without converting it again).
// Returns { duplicate, asset }.
const uploadDirectToS3 = async ({ file: originalFile, type, token, signal, onStage, onProgress, excludeId, finalizeUrl, finalizeBody, skipConversion }) => {
    if (!window.crypto?.subtle) {
        throw new Error('Secure upload is unavailable on this connection (requires HTTPS).');
    }

    onStage?.('checking');
    const checksum = await sha256Hex(originalFile);

    const dupBody = excludeId ? { checksum, exclude_id: excludeId } : { checksum };
    const dupResponse = await axios.post(`${baseURL}/asset/checkDuplicate`, dupBody, {
        headers: { 'x-access-token': token },
        signal,
    });
    if (dupResponse.data.data.duplicate) {
        return { duplicate: true, asset: dupResponse.data.data.existingAsset };
    }

    let file = originalFile;
    if (type === 'Tutorial Videos' && !skipConversion) {
        onStage?.('converting');
        file = await convertVideoForWeb({ file: originalFile, signal, onProgress });
    }

    onStage?.('uploading');
    onProgress?.(0);
    const presignResponse = await axios.post(
        `${baseURL}/asset/presignUpload`,
        { filename: file.name, type },
        { headers: { 'x-access-token': token }, signal }
    );
    const { uploadUrl, key, contentType } = presignResponse.data.data;

    await axios.put(uploadUrl, file, {
        headers: { 'Content-Type': contentType },
        signal,
        onUploadProgress: (evt) => {
            if (evt.total) onProgress?.(Math.round((evt.loaded / evt.total) * 100));
        },
    });

    onStage?.('finalizing');
    const finalizeResponse = await axios.post(
        finalizeUrl,
        { key, name: file.name, checksum, ...finalizeBody },
        { headers: { 'x-access-token': token } }
    );
    const data = finalizeResponse.data.data;
    return { duplicate: !!data.duplicate, asset: data.duplicate ? data.existingAsset : data.asset };
};

export const uploadNewAsset = ({ file, type, token, signal, onStage, onProgress }) =>
    uploadDirectToS3({
        file,
        type,
        token,
        signal,
        onStage,
        onProgress,
        finalizeUrl: `${baseURL}/asset/finalizeUpload`,
        finalizeBody: { type },
    });

// `skipConversion` is for a file that was already converted for the web.
export const replaceAsset = ({ file, assetId, type, token, signal, onStage, onProgress, skipConversion }) =>
    uploadDirectToS3({
        file,
        type,
        token,
        signal,
        onStage,
        onProgress,
        skipConversion,
        excludeId: assetId,
        finalizeUrl: `${baseURL}/asset/${assetId}/finalizeReplace`,
        finalizeBody: {},
    });
